import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  FormControl,
  FormLabel,
  Checkbox,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import CTable from "../../components/Table/table";
import {
  getSavedMessagesByIntervalRequest,
  sendSavedMessagesRequest
} from "../../modules/admin-modules/reports/Actions";
import { getTeacherSubjectsRequest } from "../../modules/admin-modules/teachers/Actions";
import Layout from "../../components/dashboard/Layout";
import LogoutMenu from "../../components/dashboard/Layout/logout-menu";
import { toast } from "react-toastify";
import Select from "react-select";

// Optimized checkbox component to prevent unnecessary re-renders
const MessageCheckbox = React.memo(({ messageId, isSelected, onToggle }) => (
  <Checkbox
    key={`checkbox-${messageId}`}
    isChecked={isSelected}
    onChange={(e) => onToggle(messageId, e.target.checked)}
  />
));

const SavedMessages = () => {
  const dispatch = useDispatch();

  // State management
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  // Redux selectors
  const savedMessages = useSelector((state) => state.reports.savedMessages);
  const reportsLoading = useSelector((state) => state.reports.isLoading);
  const teacherSubjects = useSelector((state) => state.teachers.teacherSubjects);

  // Table columns
  const columns = [
    {
      Header: "Select",
      accessor: "select",
    },
    {
      Header: "Message",
      accessor: "message",
    },
    {
      Header: "Receiver Phone",
      accessor: "receiver_phone",
    },
    {
      Header: "Created At",
      accessor: "created_at",
    },
  ];

  // Optimized message selection handler
  const handleMessageToggle = useCallback((messageId, isChecked) => {
    setSelectedMessages(prev => {
      if (isChecked) {
        return [...prev, messageId];
      } else {
        return prev.filter(id => id !== messageId);
      }
    });
  }, []);

  // Optimized table data with useMemo
  const tableData = useMemo(() => {
    return (savedMessages || [])?.map((message) => ({
      ...message,
      select: (
        <MessageCheckbox
          messageId={message.id}
          isSelected={selectedMessages.includes(message.id)}
          onToggle={handleMessageToggle}
        />
      ),
      created_at: new Date(message.created_at).toLocaleString(),
    })) || [];
  }, [savedMessages, selectedMessages, handleMessageToggle]);

  // Handle fetch saved messages
  const handleFetchMessages = useCallback(() => {
    if (!startDateTime || !endDateTime) {
      toast.error("Please select both start and end date/time");
      return;
    }

    const payload = {
      start_date_time: startDateTime,
      end_date_time: endDateTime,
      ...(selectedSubject && { user_id: selectedSubject }),
    };

    dispatch(getSavedMessagesByIntervalRequest(payload));
  }, [startDateTime, endDateTime, dispatch, selectedSubject]);

  // Handle send selected messages
  const handleSendMessages = useCallback(() => {
    if (selectedMessages.length === 0) {
      toast.error("Please select at least one message to send");
      return;
    }

    const payload = {
      message_ids: selectedMessages,
      ...(selectedSubject && { user_id: selectedSubject }),
    };

    dispatch(sendSavedMessagesRequest(payload));
    setSelectedMessages([]);
  }, [selectedMessages, dispatch, selectedSubject]);

  // Optimized handle select all messages
  const handleSelectAll = useCallback((isChecked) => {
    if (isChecked) {
      setSelectedMessages((savedMessages || [])?.map(message => message.id) || []);
    } else {
      setSelectedMessages([]);
    }
  }, [savedMessages]);

  // Format date for input fields
  const formatDateForInput = (date) => {
    return new Date(date).toISOString().slice(0, 16);
  };

  const teacherSubjectsOptions = teacherSubjects?.data?.map((subject) => ({
    value: subject.id,
    label: subject.subject.name,
  }));

  useEffect(() => {
    dispatch(getTeacherSubjectsRequest());
  }, [dispatch]);

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      background: "#f5f5f5",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
  };

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="10000000"
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            Saved Messages
          </Text>
          <LogoutMenu />
        </Flex>

        {/* Date Filters */}
        <Box mb={6} p={4} bg="gray.50" borderRadius="md">
          <Text fontSize="16px" fontWeight="500" mb={4}>
            Filter Messages by Date Range
          </Text>
          <Flex gap={4} alignItems="end">
            <FormControl>
              <FormLabel>Start Date & Time</FormLabel>
              <Input
                type="datetime-local"
                value={startDateTime}
                onChange={(e) => setStartDateTime(e.target.value)}
                placeholder="Select start date and time"
              />
            </FormControl>
            <FormControl>
              <FormLabel>End Date & Time</FormLabel>
              <Input
                type="datetime-local"
                value={endDateTime}
                onChange={(e) => setEndDateTime(e.target.value)}
                placeholder="Select end date and time"
              />
            </FormControl>
            <Button
              colorScheme="blue"
              onClick={handleFetchMessages}
              isLoading={reportsLoading}
              loadingText="Fetching..."
              disabled={!startDateTime || !endDateTime}
            >
              Fetch Messages
            </Button>
          </Flex>
        </Box>
        <Box mb={6} p={4} bg="gray.50" borderRadius="md" w="50%">
          <Select
            options={teacherSubjectsOptions}
            onChange={(data) => {
              setSelectedSubject(data.value);
            }}
            styles={colourStyles}
            placeholder="المادة"
          />
        </Box>

        {/* Selected Messages Summary */}
        {selectedMessages.length > 0 && (
          <Box mb={4} p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
            <Flex justifyContent="space-between" alignItems="center">
              <Text color="orange.800" fontWeight="500">
                {selectedMessages.length} message(s) selected
              </Text>
              <Button
                colorScheme="orange"
                onClick={handleSendMessages}
                isLoading={reportsLoading}
                loadingText="Sending..."
                disabled={selectedMessages.length === 0}
              >
                Send Selected Messages
              </Button>
            </Flex>
          </Box>
        )}

        {/* Messages Table */}
        {savedMessages && savedMessages.length > 0 && (
          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Text fontSize="18px" fontWeight="500">
                Saved Messages ({savedMessages.length})
              </Text>
              <Checkbox
                isChecked={selectedMessages.length === (savedMessages || [])?.length && (savedMessages || [])?.length > 0}
                isIndeterminate={selectedMessages.length > 0 && selectedMessages.length < ((savedMessages || [])?.length || 0)}
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                Select All
              </Checkbox>
            </Flex>

            <CTable
              Columns={columns}
              Data={tableData}
              Actions={false}
              currentpage={1}
              setPageNumber={() => { }}
              isLoading={reportsLoading}
              totalPage={1}
            />
          </Box>
        )}

        {/* No messages found */}
        {savedMessages && savedMessages.length === 0 && !reportsLoading && (
          <Box p={6} textAlign="center" bg="gray.50" borderRadius="md">
            <Text fontSize="16px" color="gray.600">
              No saved messages found for the selected date range
            </Text>
          </Box>
        )}

        {/* Instructions */}
        {!savedMessages && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Instructions</AlertTitle>
              <AlertDescription>
                Select a start and end date/time to fetch saved messages. You can then select individual messages
                or use "Select All" to choose multiple messages for sending.
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </div>
    </Layout>
  );
};

export default SavedMessages;
